import { useNavigate } from 'react-router-dom';
import { C } from './whoop-theme';
import './whoop.css';

interface IntegrationCard {
  name: string;
  mono: string;
  tileBg: string;
  tileColor: string;
  featured?: boolean;
  connected: boolean;
  statusText: string;
  sync: string;
  preview: string;
  action: string;
  route: string | null;
}

const CARDS: IntegrationCard[] = [
  {
    name: 'WHOOP',
    mono: 'W',
    tileBg: 'rgba(181,155,255,0.16)',
    tileColor: C.accent,
    featured: true,
    connected: true,
    statusText: 'Connected',
    sync: 'synced 8 min ago',
    preview:
      'Recovery 72% · HRV 58 ms · last night 6h 42m. Today: threshold session flagged for a volume cut.',
    action: 'Open dashboard',
    route: '/integrations/whoop/now',
  },
  {
    name: 'Strava',
    mono: 'S',
    tileBg: '#fc4c02',
    tileColor: '#fff',
    connected: true,
    statusText: 'Connected',
    sync: 'synced 2 h ago',
    preview: 'Last activity: 16 mi long run · 8:48/mi avg. 38 mi this week across 5 runs.',
    action: 'Open dashboard',
    route: null,
  },
  {
    name: 'Spotify',
    mono: '♪',
    tileBg: '#1db954',
    tileColor: '#04210f',
    connected: true,
    statusText: 'Connected',
    sync: 'synced 5 min ago',
    preview: 'Most-played this week: long-run playlist. 142 BPM average on easy days.',
    action: 'Open dashboard',
    route: null,
  },
  {
    name: 'GitHub',
    mono: 'G',
    tileBg: '#e8e6e1',
    tileColor: '#0c0c0e',
    connected: true,
    statusText: 'Connected',
    sync: 'synced 1 h ago',
    preview: '14 commits this week across 3 repos. Current streak: 9 days.',
    action: 'Open dashboard',
    route: null,
  },
  {
    name: 'Audible',
    mono: 'A',
    tileBg: '#f8991c',
    tileColor: '#2a1500',
    connected: false,
    statusText: 'Not connected',
    sync: 'never synced',
    preview: 'Link Audible to fold listening time into your easy-run and recovery-day context.',
    action: 'Connect',
    route: null,
  },
  {
    name: 'LinkedIn',
    mono: 'in',
    tileBg: '#2f6fed',
    tileColor: '#fff',
    connected: true,
    statusText: 'Connected',
    sync: 'synced 1 d ago',
    preview: 'Profile in sync. Not currently feeding any training widgets.',
    action: 'Open dashboard',
    route: null,
  },
];

export function IntegrationsHubPage() {
  const navigate = useNavigate();

  return (
    <main style={{ width: '100%', maxWidth: 1160, margin: '0 auto', padding: '54px 26px 90px' }}>
      <div
        style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: 11,
          letterSpacing: '0.18em',
          color: C.accent,
          textTransform: 'uppercase',
          marginBottom: 14,
        }}
      >
        Integrations
      </div>
      <h1
        style={{
          fontSize: 38,
          fontWeight: 800,
          letterSpacing: '-0.025em',
          margin: '0 0 12px',
          maxWidth: 680,
          lineHeight: 1.05,
        }}
      >
        Connected services, working for me.
      </h1>
      <p
        style={{
          fontSize: '15.5px',
          color: C.text2,
          margin: '0 0 40px',
          maxWidth: 560,
          lineHeight: 1.55,
        }}
      >
        Each integration turns raw data into something I can act on. WHOOP is the one I lean on
        daily — a personal training decision system, not another dashboard.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(312px,1fr))',
          gap: 18,
        }}
      >
        {CARDS.map((card) => (
          <HubCard key={card.name} card={card} onOpen={() => card.route && navigate(card.route)} />
        ))}
      </div>
    </main>
  );
}

function HubCard({ card, onOpen }: { card: IntegrationCard; onOpen: () => void }) {
  const actionColor = card.action === 'Connect' ? C.accent : card.route ? C.text : C.text3;
  const actionArrow = card.route ? '→' : card.action === 'Connect' ? '+' : '';
  return (
    <div
      onClick={onOpen}
      className="ww-int-card"
      style={{
        position: 'relative',
        border: `1px solid ${card.featured ? 'rgba(181,155,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
        background: card.featured
          ? 'linear-gradient(180deg,rgba(181,155,255,0.07),rgba(20,20,22,0.4))'
          : '#141416',
        borderRadius: 16,
        padding: '22px 22px 20px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 188,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 11,
              background: card.tileBg,
              color: card.tileColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 17,
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            {card.mono}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {card.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: card.connected ? C.good : C.text3,
                }}
              />
              <span style={{ fontSize: 12, color: C.text2 }}>{card.statusText}</span>
            </div>
          </div>
        </div>
        {card.featured && (
          <span
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '9.5px',
              letterSpacing: '0.12em',
              color: C.accent,
              border: '1px solid rgba(181,155,255,0.4)',
              borderRadius: 5,
              padding: '3px 7px',
            }}
          >
            DAILY
          </span>
        )}
      </div>
      <div style={{ flex: 1, fontSize: '13.5px', color: '#cbc9c2', lineHeight: 1.5 }}>
        {card.preview}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 18,
          paddingTop: 15,
          borderTop: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <span
          style={{ fontSize: '11.5px', color: C.text3, fontFamily: "'JetBrains Mono',monospace" }}
        >
          {card.sync}
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: actionColor,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          {card.action} <span style={{ fontSize: 15 }}>{actionArrow}</span>
        </span>
      </div>
    </div>
  );
}
